import type { DataSource } from '../../dataSource/dataSource'
import type { SubtaskDraft } from '../../db/models'
import type { ImportPlan, PlannedSubtask, Ref } from './plan'

/** Farbe neuer Projekte wie in der Sidebar, wenn Todoist keine bekannte nennt. */
const DEFAULT_PROJECT_COLOR = 'var(--space-500)'

export interface ImportResult {
  projects: number
  sections: number
  tags: number
  tasks: number
  completed: number
  subtasks: number
  /** Schon übernommen oder in HappyTask gelöscht — unangetastet. */
  skipped: number
}

type Writer = Pick<DataSource, 'createTag' | 'createProject' | 'createSection' | 'createTask' | 'updateTasks'>

/**
 * **Legt einen Import-Plan an** (T07, #172) — über die Datenquelle, damit der
 * Sync mitläuft. Reihenfolge: Tags, Projekte, Abschnitte, Aufgaben; jede Zeile
 * trägt ihre Herkunft. Bricht es mittendrin ab, findet ein erneuter Import das
 * schon Angelegte über `importedFrom` und macht weiter.
 */
export async function runTodoistImport(
  plan: ImportPlan,
  dataSource: Writer,
  onProgress?: (done: number, total: number) => void,
): Promise<ImportResult> {
  const ids = new Map<string, string>()
  const resolve = (ref: Ref | null): string | null => (ref === null ? null : 'id' in ref ? ref.id : ids.get(ref.key) ?? null)
  const total = plan.tags.length + plan.projects.length + plan.sections.length + plan.tasks.length
  let done = 0
  const tick = () => onProgress?.(++done, total)
  onProgress?.(0, total)

  for (const tag of plan.tags) {
    const created = await dataSource.createTag(tag.name, { importedFrom: tag.importedFrom, color: tag.color })
    ids.set(tag.key, created.id)
    tick()
  }

  /*
    Ohne `order` legt HappyTask ein neues Projekt oben in seinen Bereich. Rückwärts angelegt
    stehen die Projekte oben danach in Todoists Reihenfolge. Unterprojekte (#185) kommen danach
    vorwärts: jedes als letztes unter sein Elternprojekt — das dann schon da ist.
  */
  const roots = plan.projects.filter(p => p.parent === null).reverse()
  const children = plan.projects.filter(p => p.parent !== null)
  for (const project of [...roots, ...children]) {
    const parentId = resolve(project.parent)
    const created = await dataSource.createProject({
      name: project.name,
      description: null,
      color: project.color ?? DEFAULT_PROJECT_COLOR,
      icon: project.icon,
      iconColor: project.iconColor,
      status: project.archived ? 'archived' : 'active',
      lifeSpace: project.lifeSpace,
      startDate: null,
      endDate: null,
      progress: 0,
      importedFrom: project.importedFrom,
      // Im selben Aufruf wie das Projekt: Ein Abbruch hinterlässt kein Projekt ohne seine Kommentare (#184).
      comments: project.comments,
      ...(parentId ? { parentId } : {}),
      ...(project.favoriteOrder != null ? { favoriteOrder: project.favoriteOrder } : {}),
    })
    ids.set(project.importedFrom, created.id)
    tick()
  }

  for (const section of plan.sections) {
    const projectId = resolve(section.project)
    if (projectId) {
      const created = await dataSource.createSection({
        title: section.title,
        projectId,
        collapsed: section.collapsed,
        importedFrom: section.importedFrom,
        description: section.description,
        archivedAt: section.archivedAt,
        ...(section.order !== null ? { order: section.order } : {}),
      })
      ids.set(section.importedFrom, created.id)
    }
    tick()
  }

  const tagIds = (refs: Ref[]) => refs.map(resolve).filter((id): id is string => id !== null)
  const draftOf = (sub: PlannedSubtask): SubtaskDraft => ({ ...sub, tags: tagIds(sub.tags), subtasks: sub.subtasks.map(draftOf) })
  const countOf = (subs: PlannedSubtask[]): number => subs.reduce((sum, sub) => sum + 1 + countOf(sub.subtasks), 0)

  let completed = 0
  let subtasks = 0
  for (const task of plan.tasks) {
    await dataSource.createTask({
      title: task.title,
      description: task.description,
      status: task.completedAt ? 'completed' : 'todo',
      attention: task.attention,
      category: 'other',
      lifeSpace: task.lifeSpace,
      projectId: resolve(task.project),
      sectionId: resolve(task.section),
      startDate: task.startDate,
      startTime: task.startTime,
      dueDate: task.dueDate,
      completedAt: task.completedAt,
      estimatedDuration: task.estimatedDuration,
      actualDuration: null,
      durationMode: task.durationMode,
      recurrence: task.recurrence,
      completedCount: task.completedCount,
      postponedCount: task.postponedCount,
      tags: tagIds(task.tags),
      parentTaskId: null,
      pausedAt: null,
      importedFrom: task.importedFrom,
      // Im selben Aufruf wie die Aufgabe: Bricht der Import ab, fehlt keiner übernommenen Aufgabe
      // ein Kommentar, den ein zweiter Lauf nicht mehr brächte — er überspringt die Aufgabe (#183).
      comments: task.comments,
      // Gesetzt, auch leer: Was eine importierte Aufgabe bekommt, entscheidet der Plan (`withAutoReminder`), nicht die Datenschicht.
      reminders: task.reminders,
      subtasks: task.subtasks.map(draftOf),
      ...(task.order !== null ? { order: task.order } : {}),
    })
    if (task.completedAt) completed++
    subtasks += countOf(task.subtasks)
    tick()
  }

  // Was ein früherer Import liegen ließ (Tages-Dauern #197, Zähler #198) — über die Datenquelle, damit
  // der Sync mitläuft. Die Zähler kommen mitgebracht an und zählen dort nicht weiter.
  if (plan.taskFills.length > 0) await dataSource.updateTasks(plan.taskFills)

  return {
    projects: plan.projects.length,
    sections: plan.sections.length,
    tags: plan.tags.length,
    tasks: plan.tasks.length - completed,
    completed,
    subtasks,
    skipped: plan.skipped,
  }
}
